import noop from 'lodash/noop';

import { mergeLogOptions, toLoggableError } from './errors/LoggableError';
import logError, { LogErrorOptions } from './log-error';
import { logEventFloat, logEventInteger, logEventString, LogEventStringOptions } from './log-event';
import { ACTION, COUNTERS, FEATURE, STATUS } from './logger-enums';
import { configureLogger, Logger } from './loggerFacade';
import { countMetrika } from './metrika';
import isOnPremise from './onpremise/is-onpremise';

const additionalProviders: Array<() => LogErrorOptions['additional']> = [];

export const addAdditionalProviders = (provider: () => LogErrorOptions['additional']) => {
    additionalProviders.push(provider);
};

const getAdditionalFromProviders = () => {
    return additionalProviders.reduce((aux, provider) => {
        return {
            ...aux,
            ...provider(),
        };
    }, {});
};

function emitFeatureEventNumeric(
    emitter: typeof logEventInteger | typeof logEventFloat,
    block: string,
    feature: FEATURE,
    action: ACTION,
    value: number,
    isSuccess: boolean | undefined,
    options: LogEventStringOptions | undefined
): void {
    const statusSuffix = isSuccess === undefined ? '' : isSuccess ? `_${STATUS.SUCCESS}` : `_${STATUS.FAIL}`;
    const name = `${feature}_${action}_event${statusSuffix}`;
    const mergedOptions = {
        block,
        ...options,
        additional: {
            ...getAdditionalFromProviders(),
            ...options?.additional,
        },
    };

    try {
        emitter(name, value, mergedOptions);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
    }
}

function createOnPremLogger(block: string): Logger {
    return {
        // eslint-disable-next-line no-console
        info: (...args) => console.info(block, ...args),
        // eslint-disable-next-line no-console
        debug: (...args) => console.debug(block, ...args),
        // eslint-disable-next-line no-console
        log: (...args) => console.log(block, ...args),
        // eslint-disable-next-line no-console
        warn: (...args) => console.warn(block, ...args),
        // eslint-disable-next-line no-console
        error: (...args) => console.error(block, ...args),
        event: noop,
        featureEvent: noop,
        featureEventInteger: noop,
        featureEventFloat: noop,
        startDelta: noop,
        endDelta: noop,
        catch(options) {
            return (error) => {
                this.error(error, options);
            };
        },
        startFeatureDelta: noop,
        endFeatureDelta: noop,
        discardFeatureDelta: noop,
    };
}

const timeouts: { [key: string]: TimeoutType } = {};

interface DeltaMetricState {
    startTime: number;
    isCompleted: boolean;
    timeoutId?: TimeoutType;
    options?: LogEventStringOptions;
}

//TODO: Добавить block(из createCloudLogger) в key deltaMetrics https://st.yandex-team.ru/TELEMOSTFRONT-2864
const deltaMetrics = new Map<string, DeltaMetricState>();
const DEFAULT_SEND_DELTA_TIMEOUT = 40000;

function createCloudLogger(block: string): Logger {
    const logger: Logger = {
        // eslint-disable-next-line no-console
        info: (...args) => console.info(block, ...args),
        // eslint-disable-next-line no-console
        debug: (...args) => console.debug(block, ...args),
        // eslint-disable-next-line no-console
        log: (...args) => console.log(block, ...args),

        catch(options) {
            return (error: unknown) => {
                this.error(error, options);
            };
        },
        warn: (message: unknown, logFieldsOrAny: LogErrorOptions = {}) => {
            const loggableMessage = toLoggableError(message);
            const sourceFields = loggableMessage.loggerFields;
            const logFields = typeof logFieldsOrAny === 'object' ? logFieldsOrAny : undefined;

            const {
                metrikaName = undefined,
                metrikaParams = [],
                ...fields
            } = mergeLogOptions(
                sourceFields,
                {
                    block,
                    method: sourceFields?.method || 'unknown',
                    level: 'warning',
                    ...logFields,
                },
                getAdditionalFromProviders()
            );

            logError(fields, loggableMessage);

            // eslint-disable-next-line no-console
            console.warn(block, message, fields);

            if (metrikaName) {
                countMetrika(metrikaName, ...metrikaParams);
            }
        },
        error: (error: unknown, logFieldsOrAny: LogErrorOptions = {}) => {
            const loggableError = toLoggableError(error);
            const sourceFields = loggableError.loggerFields;
            const logFields = typeof logFieldsOrAny === 'object' ? logFieldsOrAny : undefined;

            const {
                metrikaName = undefined,
                metrikaParams = [],
                ...fields
            } = mergeLogOptions(
                sourceFields,
                {
                    block,
                    method: sourceFields?.method || 'unknown',
                    level: 'error',
                    ...logFields,
                },
                getAdditionalFromProviders()
            );

            logError(fields, loggableError);

            // eslint-disable-next-line no-console
            console.error(block, error, fields);

            if (metrikaName) {
                countMetrika(metrikaName, ...metrikaParams);
            }
        },
        event: (name: string, options?: LogEventStringOptions) => {
            const mergedOptions = {
                block,
                ...options,
                additional: {
                    ...getAdditionalFromProviders(),
                    ...options?.additional,
                },
            };

            logEventString(name, mergedOptions);

            // eslint-disable-next-line no-console
            console.debug(block, name, mergedOptions);
        },
        featureEvent: ({ feature, action, options, isSuccess, timeoutMS, onFail }) => {
            const mergedOptions = {
                ...options,
                additional: encodeURIComponent(
                    JSON.stringify({
                        ...getAdditionalFromProviders(),
                        ...options?.additional,
                    })
                ),
            };

            const prefix = `${feature}_${action}_event`;
            const eventName = `${prefix}${
                isSuccess !== undefined ? (isSuccess ? `_${STATUS.SUCCESS}` : `_${STATUS.FAIL}`) : ''
            }`;
            try {
                window?.Ya.Rum.sendTimeMark(`${eventName}`, undefined, undefined, mergedOptions);

                if (isSuccess !== undefined && timeouts[prefix] !== undefined) {
                    clearTimeout(timeouts[prefix]);
                    delete timeouts[prefix];
                }

                if (timeoutMS !== undefined) {
                    timeouts[prefix] = setTimeout(() => {
                        onFail?.();
                        const failByTimeoutMergedOptions = {
                            ...options,
                            additional: encodeURIComponent(
                                JSON.stringify({
                                    ...getAdditionalFromProviders(),
                                    ...options?.additional,
                                    fail_by_timeout: true,
                                })
                            ),
                        };
                        window?.Ya.Rum.sendTimeMark(
                            `${prefix}_${STATUS.FAIL}`,
                            undefined,
                            undefined,
                            failByTimeoutMergedOptions
                        );
                        delete timeouts[prefix];
                    }, timeoutMS);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        featureEventInteger: ({ feature, action, value, isSuccess, options }) => {
            emitFeatureEventNumeric(logEventInteger, block, feature, action, value, isSuccess, options);
        },
        featureEventFloat: ({ feature, action, value, isSuccess, options }) => {
            emitFeatureEventNumeric(logEventFloat, block, feature, action, value, isSuccess, options);
        },
        startDelta: (counterId: COUNTERS) => {
            try {
                window?.Ya.Rum.time(counterId);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        endDelta: (counterId: COUNTERS, vars?: object) => {
            try {
                window?.Ya.Rum.timeEnd(counterId, {
                    additional: encodeURIComponent(
                        JSON.stringify({
                            ...getAdditionalFromProviders(),
                            ...vars,
                        })
                    ),
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        startFeatureDelta: ({ feature, action, id, options, timeoutMS = DEFAULT_SEND_DELTA_TIMEOUT, onFail }) => {
            const key = `${feature}_${action}_${id || 'default'}`;

            try {
                const previousState = deltaMetrics.get(key);

                if (previousState && previousState.timeoutId) {
                    clearTimeout(previousState.timeoutId);
                }

                const startTime = Date.now();

                const state: DeltaMetricState = {
                    startTime,
                    isCompleted: false,
                    options,
                };

                if (timeoutMS && Number.isFinite(timeoutMS) && timeoutMS > 0) {
                    state.timeoutId = setTimeout(() => {
                        const state = deltaMetrics.get(key);

                        if (state && state.isCompleted) {
                            return;
                        }

                        onFail?.();

                        logger.endFeatureDelta({
                            feature,
                            action,
                            id,
                            isSuccess: false,
                            options: {
                                ...options,
                                additional: {
                                    ...options?.additional,
                                    fail_by_timeout: true,
                                },
                            },
                        });
                    }, timeoutMS);
                }

                deltaMetrics.set(key, state);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
        endFeatureDelta: ({ feature, action, id, isSuccess, options }) => {
            const key = `${feature}_${action}_${id || 'default'}`;
            const prefix = `${feature}_${action}_delta`;

            const state = deltaMetrics.get(key);

            if (!state) {
                return;
            }

            if (state.isCompleted) {
                return;
            }

            try {
                const currentTime = Date.now();
                const delta = currentTime - state.startTime;

                const mergedOptions = {
                    ...state.options,
                    ...options,
                    additional: encodeURIComponent(
                        JSON.stringify({
                            ...getAdditionalFromProviders(),
                            ...state.options?.additional,
                            ...options?.additional,
                        })
                    ),
                };

                const metricName = `${prefix}_${isSuccess ? STATUS.SUCCESS : STATUS.FAIL}`;
                window?.Ya.Rum.sendDelta(metricName, delta, mergedOptions);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            } finally {
                if (state.timeoutId) {
                    clearTimeout(state.timeoutId);
                }

                state.isCompleted = true;

                deltaMetrics.delete(key);
            }
        },
        discardFeatureDelta: ({ feature, action, id }) => {
            const key = `${feature}_${action}_${id || 'default'}`;
            const state = deltaMetrics.get(key);

            if (!state) {
                return;
            }

            try {
                if (state.timeoutId) {
                    clearTimeout(state.timeoutId);
                }

                deltaMetrics.delete(key);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(error);
            }
        },
    };

    return logger;
}

configureLogger(isOnPremise ? createOnPremLogger : createCloudLogger);
